import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { translations, type Locale, type Translation } from "@/i18n/translations";

interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number; // rate from USD
}

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translation;
  currency: CurrencyInfo;
  formatLocalPrice: (usdPrice: number) => string;
  isRTL: boolean;
}

const defaultCurrency: CurrencyInfo = { code: "USD", symbol: "$", rate: 1 };

// Country → currency
const currencyMap: Record<string, { code: string; symbol: string }> = {
  US: { code: "USD", symbol: "$" },
  TH: { code: "THB", symbol: "฿" },
  GB: { code: "GBP", symbol: "£" },
  EU: { code: "EUR", symbol: "€" },
  DE: { code: "EUR", symbol: "€" },
  FR: { code: "EUR", symbol: "€" },
  ES: { code: "EUR", symbol: "€" },
  IT: { code: "EUR", symbol: "€" },
  NL: { code: "EUR", symbol: "€" },
  AT: { code: "EUR", symbol: "€" },
  BE: { code: "EUR", symbol: "€" },
  PT: { code: "EUR", symbol: "€" },
  IE: { code: "EUR", symbol: "€" },
  GR: { code: "EUR", symbol: "€" },
  JP: { code: "JPY", symbol: "¥" },
  CN: { code: "CNY", symbol: "¥" },
  KR: { code: "KRW", symbol: "₩" },
  BR: { code: "BRL", symbol: "R$" },
  MX: { code: "MXN", symbol: "$" },
  IN: { code: "INR", symbol: "₹" },
  SA: { code: "SAR", symbol: "﷼" },
  AE: { code: "AED", symbol: "د.إ" },
  MY: { code: "MYR", symbol: "RM" },
  SG: { code: "SGD", symbol: "S$" },
  PH: { code: "PHP", symbol: "₱" },
  ID: { code: "IDR", symbol: "Rp" },
  VN: { code: "VND", symbol: "₫" },
  AU: { code: "AUD", symbol: "A$" },
  CA: { code: "CAD", symbol: "C$" },
  AR: { code: "ARS", symbol: "$" },
  CL: { code: "CLP", symbol: "$" },
  CO: { code: "COP", symbol: "$" },
  EG: { code: "EGP", symbol: "E£" },
  NG: { code: "NGN", symbol: "₦" },
  ZA: { code: "ZAR", symbol: "R" },
  TR: { code: "TRY", symbol: "₺" },
  RU: { code: "RUB", symbol: "₽" },
  PL: { code: "PLN", symbol: "zł" },
  SE: { code: "SEK", symbol: "kr" },
  NO: { code: "NOK", symbol: "kr" },
  DK: { code: "DKK", symbol: "kr" },
  CH: { code: "CHF", symbol: "CHF" },
  TW: { code: "TWD", symbol: "NT$" },
  HK: { code: "HKD", symbol: "HK$" },
  NZ: { code: "NZD", symbol: "NZ$" },
};

// Country → preferred UI language. Defaults to "en" if not listed.
const countryToLocale: Record<string, Locale> = {
  // English-native — keep English
  US: "en", GB: "en", AU: "en", CA: "en", NZ: "en", IE: "en", ZA: "en",
  // Spanish
  ES: "es", MX: "es", AR: "es", CL: "es", CO: "es", PE: "es", VE: "es", EC: "es",
  // Chinese
  CN: "zh", TW: "zh", HK: "zh", SG: "zh",
  // Arabic
  SA: "ar", AE: "ar", EG: "ar", QA: "ar", KW: "ar", BH: "ar", OM: "ar", JO: "ar", MA: "ar",
  // French
  FR: "fr", BE: "fr", CH: "fr", LU: "fr", MC: "fr",
  // Portuguese
  BR: "pt", PT: "pt",
  // Japanese
  JP: "ja",
  // Korean
  KR: "ko",
  // German
  DE: "de", AT: "de",
  // Thai
  TH: "th",
  // Hindi (India)
  IN: "hi",
  // Vietnamese
  VN: "vi",
  // Indonesian
  ID: "id",
  // Turkish
  TR: "tr",
  // Russian
  RU: "ru", BY: "ru", KZ: "ru", KG: "ru",
  // Italian
  IT: "it",
  // Polish
  PL: "pl",
  // Dutch
  NL: "nl",
  // Swedish
  SE: "sv",
  // Filipino
  PH: "fil",
};

const I18nContext = createContext<I18nState | null>(null);

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    // 1. Check URL for locale prefix first (e.g., /de/, /es/)
    const pathLocale = window.location.pathname.split("/")[1];
    if (pathLocale && pathLocale in translations) return pathLocale as Locale;
    // 2. Check saved preference
    const saved = localStorage.getItem("tokdadddy-locale");
    if (saved && saved in translations) return saved as Locale;
    // 3. Auto-detect from browser language
    const browserLangs = navigator.languages || [navigator.language];
    for (const lang of browserLangs) {
      const short = lang.split("-")[0].toLowerCase();
      if (short in translations) return short as Locale;
    }
    return "en";
  });

  // On first visit, redirect to detected locale URL if not already prefixed
  useEffect(() => {
    const hasVisited = localStorage.getItem("tokdadddy-locale");
    if (hasVisited) return; // Not first visit
    const pathLocale = window.location.pathname.split("/")[1];
    if (pathLocale && pathLocale in translations) return; // Already on a locale URL
    if (locale !== "en") {
      const currentPath = window.location.pathname;
      const newPath = `/${locale}${currentPath === "/" ? "" : currentPath}`;
      window.history.replaceState(null, "", newPath + window.location.search);
    }
    localStorage.setItem("tokdadddy-locale", locale);
  }, []);

  const [currency, setCurrency] = useState<CurrencyInfo>(defaultCurrency);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem("tokdadddy-locale", l);
  }, []);

  // Detect user country, set currency + (on first visit) auto-switch language
  useEffect(() => {
    const detect = async () => {
      try {
        const geoRes = await fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(5000) });
        if (!geoRes.ok) return;
        const geoData = await geoRes.json();
        const countryCode = (geoData.country_code as string)?.toUpperCase();
        if (!countryCode) return;

        // --- 1. Auto-switch UI language on first visit only ---
        const hasSavedLocale = localStorage.getItem("tokdadddy-locale");
        const pathLocale = window.location.pathname.split("/")[1];
        const onLocalizedUrl = pathLocale && pathLocale in translations;
        if (!hasSavedLocale && !onLocalizedUrl) {
          const detectedLocale = countryToLocale[countryCode];
          if (detectedLocale && detectedLocale !== locale) {
            setLocaleState(detectedLocale);
            localStorage.setItem("tokdadddy-locale", detectedLocale);
            if (detectedLocale !== "en") {
              const currentPath = window.location.pathname;
              const newPath = `/${detectedLocale}${currentPath === "/" ? "" : currentPath}`;
              window.history.replaceState(null, "", newPath + window.location.search);
            }
          }
        }

        // --- 2. Currency detection (independent of language) ---
        const mapped = currencyMap[countryCode];
        if (!mapped || mapped.code === "USD") {
          setCurrency({ code: "USD", symbol: "$", rate: 1 });
          return;
        }
        const rateRes = await fetch(
          `https://open.er-api.com/v6/latest/USD`,
          { signal: AbortSignal.timeout(5000) }
        );
        if (!rateRes.ok) return;
        const rateData = await rateRes.json();
        const rate = rateData.rates?.[mapped.code];
        if (rate) {
          setCurrency({ code: mapped.code, symbol: mapped.symbol, rate });
        }
      } catch {
        // Silently fail - keep defaults
      }
    };

    detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = translations[locale];
  const isRTL = locale === "ar";

  // Apply per-language script font + dir to <html>
  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    html.dir = isRTL ? "rtl" : "ltr";
    // Remove any previous lang-XX classes, add current
    html.className = html.className.replace(/\blang-\w+\b/g, "").trim();
    html.classList.add(`lang-${locale}`);
  }, [locale, isRTL]);

  const formatLocalPrice = useCallback(
    (usdPrice: number) => {
      if (currency.code === "USD") return `$${usdPrice.toFixed(2)}`;
      const localPrice = usdPrice * currency.rate;
      // Format with appropriate decimal places
      const formatted = localPrice >= 100
        ? Math.round(localPrice).toLocaleString()
        : localPrice.toFixed(2);
      return `~${currency.symbol}${formatted}`;
    },
    [currency]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, currency, formatLocalPrice, isRTL }}>
      {children}
    </I18nContext.Provider>
  );
};
